import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Timer } from 'lucide-react';
import type { Tunable } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { Button, Card, ErrorNotice, Input, Spinner } from '../../components/ui';

/**
 * The timings an operator can change without a deploy.
 *
 * Its neighbour, the Configuration card, stays read-only on purpose: those
 * values say where the platform is installed, and a typo in the CWMP URL takes
 * the fleet offline. These six say how often it does things — how often the
 * chassis are swept, how long an ONU counts as online, when the nightly work
 * runs — and getting one wrong costs a slow night, not an outage.
 *
 * Each row says where its value came from. An operator who set the sweep to
 * ten minutes last spring should be able to see that they did, rather than
 * wondering whether the server was deployed that way.
 */
export function TimingsCard() {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['settings', 'tunables'],
    queryFn: () => api.get<{ items: Tunable[] }>('/settings/tunables'),
  });

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: number | null }) =>
      api.patch<{ items: Tunable[] }>(`/settings/tunables/${encodeURIComponent(key)}`, { value }),
    onSuccess: async (result, variables) => {
      setError(null);
      // Drop the draft rather than leaving it to shadow the saved value: the
      // server may have stored something the operator did not type.
      setDrafts((d) => {
        const next = { ...d };
        delete next[variables.key];
        return next;
      });
      queryClient.setQueryData(['settings', 'tunables'], result);
      // The system card shows the online window too, and it just moved.
      await queryClient.invalidateQueries({ queryKey: ['system-info'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save the setting'),
  });

  const items = query.data?.items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Timer className="size-4 text-muted" />
          Timings
        </span>
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : query.error ? (
        <div className="p-5">
          <ErrorNotice error={query.error} />
        </div>
      ) : (
        <>
          {error && (
            <div className="px-5 pt-4">
              <ErrorNotice error={error} />
            </div>
          )}
          <dl className="grid">
            {items.map((item) => {
              const draft = drafts[item.key];
              const shown = draft ?? String(item.value);
              const parsed = Number(shown);
              const valid =
                shown.trim() !== '' &&
                Number.isInteger(parsed) &&
                parsed >= item.min &&
                parsed <= item.max;
              const changed = draft !== undefined && parsed !== item.value;
              const busy = save.isPending && save.variables?.key === item.key;

              return (
                <div key={item.key} className="border-b border-line px-5 py-3">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-sm text-ink">{item.label}</dt>
                    <dd className="flex shrink-0 items-center gap-2">
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={item.min}
                        max={item.max}
                        value={shown}
                        aria-label={item.label}
                        aria-invalid={!valid}
                        className="h-8 w-24 text-right font-mono text-xs tabular-nums"
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [item.key]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && valid && changed) {
                            save.mutate({ key: item.key, value: parsed });
                          }
                          if (e.key === 'Escape') {
                            setDrafts((d) => {
                              const next = { ...d };
                              delete next[item.key];
                              return next;
                            });
                          }
                        }}
                      />
                      <span className="w-14 text-xs text-faint">{item.unit}</span>
                      {changed ? (
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={!valid}
                          loading={busy}
                          onClick={() => save.mutate({ key: item.key, value: parsed })}
                        >
                          Save
                        </Button>
                      ) : item.source === 'operator' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<RotateCcw className="size-3.5" />}
                          loading={busy}
                          title="Put this back to the value in .env"
                          onClick={() => save.mutate({ key: item.key, value: null })}
                        >
                          Reset
                        </Button>
                      ) : (
                        <span className="w-[4.5rem] text-right text-[11px] text-faint">
                          from .env
                        </span>
                      )}
                    </dd>
                  </div>
                  <p className="mt-1 pr-2 text-[11px] leading-relaxed text-faint">
                    {item.hint}
                    {!valid && (
                      <span className="text-danger">
                        {' '}
                        Between {item.min} and {item.max}.
                      </span>
                    )}
                  </p>
                </div>
              );
            })}
          </dl>
          <p className="px-5 py-2.5 text-[11px] text-faint">
            Changes take effect within a minute — no restart. Everything else the platform runs on
            lives in .env, where changing it means a deploy.
          </p>
        </>
      )}
    </Card>
  );
}
